import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../utils/authFetch/authFetch";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
// CommunityCard removed from usage in new layout
import CreateCommunityModal from "./components/CreateCommunityModal";
import SkeletonCard from "../../components/utilsComponents/SkeletonCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CommunitiesPage = ({ collapsed, setCollapsed, user }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  // filters hidden in this layout
  const [showFilters, setShowFilters] = useState(false);

  const allTags = [
    "tech",
    "startup",
    "ai",
    "web3",
    "design",
    "community",
    "education",
    "health",
  ];

  useEffect(() => {
    fetchCommunities();
  }, [searchTerm, selectedTag, sortBy]);

  const fetchCommunities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (selectedTag) params.append("tags", selectedTag);
      if (sortBy) params.append("sortBy", sortBy);
      params.append("limit", "50");

      const res = await authFetch(
        `${BACKEND_URL}/communities?${params.toString()}`,
        {},
        navigate
      );

      const data = await res.json();
      if (res.ok && data.communities) {
        setCommunities(data.communities);
      }
    } catch (error) {
      console.error("Error fetching communities:", error);
    } finally {
      setLoading(false);
    }
  };

  // No explicit create button in this layout; keep modal wiring for future use

  const handleCommunityCreated = (newCommunity) => {
    setCommunities([newCommunity, ...communities]);
    setShowCreateModal(false);
  };

  const recommended = communities.slice(0, 4);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Simple top banner */}
      <div className="w-full bg-gradient-to-r from-blue-500 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 text-white flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Communities</h1>
            <p className="mt-2 text-blue-100 text-sm md:text-base">Discover and join groups that match your interests.</p>
          </div>
          <div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-white/95 text-blue-700 hover:bg-white px-4 py-2 font-semibold shadow-sm"
            >
              <PlusIcon className="w-5 h-5" />
              Create Community
            </button>
          </div>
        </div>
      </div>

      {/* Body two-column layout */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 pb-12">
        {/* Left column */}
        <section className="lg:col-span-8 space-y-8">
          {/* Recommended communities */}
          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-3">Recommended Communities</h2>
            {loading ? (
              <div className="flex gap-4 overflow-x-auto pb-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="min-w-[240px] w-[240px]"><SkeletonCard /></div>
                ))}
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2">
                {recommended.map((c) => (
                  <MiniCommunityCard
                    key={c.id}
                    community={c}
                    onClick={() => navigate(`/communities/${c.slug || c.id}`)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Communities list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Communities</h2>
              <div className="flex-1 mx-4 max-w-md relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for Communities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
              {/* Optional filter/sort dropdowns hidden for now */}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : communities.length === 0 ? (
              <div className="text-center py-12 text-gray-600">No communities found.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {communities.map((c) => (
                  <ListCommunityRow
                    key={c.id}
                    community={c}
                    onClick={() => navigate(`/communities/${c.slug || c.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Right column placeholder (chat/assistant area) */}
        <aside className="lg:col-span-4">
          <div className="rounded-2xl border border-gray-200 bg-white h-[560px] p-4 relative">
            <div className="absolute left-6 top-6 flex gap-3">
              <span className="w-6 h-6 bg-black rounded-full inline-block" />
              <span className="w-4 h-4 bg-black rounded-full inline-block self-end" />
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center text-lg font-semibold shadow">C</div>
                <input
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm outline-none"
                  placeholder="what are you looking for ?"
                />
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Create Community Modal */}
      {showCreateModal && (
        <CreateCommunityModal
          onClose={() => setShowCreateModal(false)}
          onCommunityCreated={handleCommunityCreated}
        />
      )}
    </div>
  );
};

export default CommunitiesPage;

// Small horizontally scrollable recommended card
const MiniCommunityCard = ({ community, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="min-w-[240px] w-[240px] bg-white rounded-xl border border-gray-200 hover:shadow-sm transition text-left"
    >
      <div className="h-16 rounded-t-xl bg-gradient-to-r from-indigo-300 to-indigo-500" />
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded-md bg-gray-900 text-white flex items-center justify-center text-xs font-bold">
            {community.name?.charAt(0) || "C"}
          </div>
          <div className="text-sm font-semibold text-gray-800 line-clamp-1">{community.name}</div>
        </div>
        <p className="text-xs text-gray-600 line-clamp-2 mb-2">{community.description || "Maker Space initiative. Organized to apply for SFAB grant"}</p>
        <div className="flex -space-x-1">
          {[0,1,2].map((i) => (
            <span key={i} className="w-5 h-5 rounded-full bg-gray-200 inline-block border border-white" />
          ))}
          <span className="ml-2 text-[10px] text-gray-500">+10 friends</span>
        </div>
      </div>
    </button>
  );
};

// List row style community item
const ListCommunityRow = ({ community, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl border border-gray-200 hover:shadow-sm transition text-left p-4 flex items-start gap-4"
    >
      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xl font-bold text-gray-700">
        {community.name?.charAt(0) || "C"}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 line-clamp-1">{community.name}</h3>
          <span className="text-xs text-gray-500">view more</span>
        </div>
        <p className="mt-1 text-sm text-gray-600 line-clamp-2">{community.description || "Free local events and activities called Adventures for families who have a child facing a life‑threatening condition"}</p>
      </div>
    </button>
  );
};
